import * as THREE from 'three'
import { MeshBVH, INTERSECTED, NOT_INTERSECTED } from 'three-mesh-bvh'
import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js'

export interface RayHit {
  distance: number
  point: THREE.Vector3
  normal: THREE.Vector3
}

export interface Contact {
  push: THREE.Vector3   // world-space displacement to depenetrate
  normal: THREE.Vector3 // averaged contact normal (normalized)
  deepest: number       // deepest single-triangle penetration
}

// Static world collider built from merged, world-transformed mesh geometries.
// Server-side equivalent would maintain the same BVH off a shared map descriptor.
export class CollisionWorld {
  readonly bvh: MeshBVH
  readonly mergedMesh: THREE.Mesh

  private readonly _tmpClosest = new THREE.Vector3()
  private readonly _tmpDiff = new THREE.Vector3()
  private readonly _tmpNormal = new THREE.Vector3()
  private readonly _tmpSphere = new THREE.Sphere()

  constructor(meshes: THREE.Mesh[]) {
    const geoms: THREE.BufferGeometry[] = []
    for (const m of meshes) {
      m.updateMatrixWorld(true)
      const g = m.geometry.clone()
      if (g.attributes['uv']) g.deleteAttribute('uv')
      if (g.attributes['color']) g.deleteAttribute('color')
      g.applyMatrix4(m.matrixWorld)
      if (!g.attributes['normal']) g.computeVertexNormals()
      geoms.push(g)
    }
    const merged = BufferGeometryUtils.mergeGeometries(geoms, false)
    if (!merged) throw new Error('Failed to merge collision geometries')
    this.bvh = new MeshBVH(merged)
    this.mergedMesh = new THREE.Mesh(merged)
  }

  // Cast a ray, return first hit within maxDist or null.
  raycast(origin: THREE.Vector3, dir: THREE.Vector3, maxDist: number): RayHit | null {
    const ray = new THREE.Ray(origin, dir)
    const hit = this.bvh.raycastFirst(ray, THREE.FrontSide)
    if (!hit || hit.distance > maxDist) return null
    return {
      distance: hit.distance,
      point: hit.point.clone(),
      normal: hit.face?.normal.clone() ?? new THREE.Vector3(0, 1, 0),
    }
  }

  // Find all triangles penetrating a sphere centered at `pos` and accumulate
  // a depenetration push + an averaged contact normal. Returns null if no overlap.
  depenetrateSphere(pos: THREE.Vector3, radius: number): Contact | null {
    const sphere = this._tmpSphere.set(pos, radius)
    const closest = this._tmpClosest
    const diff = this._tmpDiff
    const triNormal = this._tmpNormal
    const push = new THREE.Vector3()
    const normalAccum = new THREE.Vector3()
    let count = 0
    let deepest = 0

    this.bvh.shapecast({
      intersectsBounds: (box: THREE.Box3) => {
        return box.intersectsSphere(sphere) ? INTERSECTED : NOT_INTERSECTED
      },
      intersectsTriangle: (tri: THREE.Triangle) => {
        tri.closestPointToPoint(pos, closest)
        diff.subVectors(pos, closest)
        const dist = diff.length()
        if (dist < radius) {
          const pen = radius - dist
          if (dist > 1e-5) {
            diff.divideScalar(dist)
          } else {
            tri.getNormal(triNormal)
            diff.copy(triNormal)
          }
          push.addScaledVector(diff, pen)
          normalAccum.add(diff)
          if (pen > deepest) deepest = pen
          count++
        }
        return false
      },
    })

    if (count === 0) return null
    normalAccum.normalize()
    return { push, normal: normalAccum, deepest }
  }
}
